<template>
  <FormSubmitPopup
    :title="title"
    v-model="value"
    @popupClose="$emit('input', false)"
    :rightMethod="saveProduct"
    hasAlert
    medium>
    <template v-slot:input>
      <v-container fluid>
        <v-list subheader>
          <v-list-tile>
            <v-layout row no-wrap>
              <v-flex v-if="isLinkMode && isLinkExistProduct">
                <v-autocomplete
                  prepend-icon="devices_other"
                  :items="productsWithoutUPC"
                  item-text="name"
                  return-object
                  :filter="customFilter"
                  @input="selectExistProduct"
                  :rules="[fieldIsRequired('Name')]"
                  class="required_field"
                  :label="$t('name')"
                  clearable
                >
                  <template v-slot:selection="data">
                    <div>{{data.item.id.slice(-5)}} - {{data.item.condition && data.item.condition.toUpperCase()}} - {{data.item.name}}</div>
                  </template>
                  <template v-slot:item="data">
                    <div :id="data.item.id">{{data.item.id.slice(-5)}} - {{data.item.condition && data.item.condition.toUpperCase()}} - {{data.item.name}}</div>
                  </template>
                </v-autocomplete>
              </v-flex>
              <v-flex v-else>
                <v-text-field
                  id="product_name"
                  :label="$t('name')"
                  v-model="productInEdit.name"
                  prepend-icon="devices_other"
                  :hint="editMode ? $t('note:UpdatedProductNameWillOnlyReflectInNewOffers') : ''"
                  persistent-hint
                  :rules="[fieldIsRequired('Name')]"
                  class="required_field"></v-text-field>
              </v-flex>
              <div class="ml-3" :style="{width: '166px'}" v-if="isLinkMode">
                <v-checkbox :label="$t('useExistProduct')" v-model="isLinkExistProduct"></v-checkbox>
              </div>
            </v-layout>
          </v-list-tile>
        </v-list>
        <br/>
        <v-layout warp justify-space-between>
          <v-flex sm5>
            <v-list subheader>
              <v-list-tile>
                <v-text-field
                  id="product_price"
                  :label="$t('price')"
                  prepend-icon="attach_money"
                  :rules="[fieldIsNumber(), fieldIsNoLessThanZero()]"
                  v-model.number="productInEdit.price"></v-text-field>
              </v-list-tile>
            </v-list>
          </v-flex>
          <v-flex sm5>
            <v-list subheader>
              <v-list-tile>
                <v-select
                  id="product_condition"
                  :items="conditions"
                  v-model="productInEdit.condition"
                  :label="$t('condition')"
                  prepend-icon="thumbs_up_down"
                  class="required_field"
                  :rules="[fieldIsRequired('condition')]"
                  clearable></v-select>
              </v-list-tile>
            </v-list>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex class="mx-3">
            <v-combobox
              id="product_asin"
              v-model="productInEdit.asin"
              label="ASIN"
              chips
              multiple
              deletable-chips
              prepend-icon="filter_list"
              clearable></v-combobox>
          </v-flex>
        </v-layout>
        <v-layout class="mb-1">
          <v-flex class="mx-3">
            <v-combobox
              v-model="productInEdit.sku"
              label="SKU"
              chips
              multiple
              deletable-chips
              prepend-icon="filter_list"
              clearable
              :rules="[checkSkuDup]"></v-combobox>
          </v-flex>
        </v-layout>
        <v-layout class="mx-3" warp justify-space-between align-baseline>
          <v-flex sm10>
            <v-text-field
              :id="'upc'"
              label="UPC"
              ref="UPC"
              v-model="displayUPC"
              prepend-icon="bookmarks"
              :readonly="!productInEdit.isUpcChangeable || upcReadOnly || !!(productInEdit.approvedSkus && productInEdit.approvedSkus.length)"
              :title="$t('productEditUpc')"
              :hint="!productInEdit.isUpcChangeable ? $t('productEditWarning') : $t('case-sensitive', {field: 'UPC'})"
              :rules="[rules.upcValid, rules.upcDup]"></v-text-field></v-flex>
          <v-flex sm2>
            <LoaderButton
              flat
              :promiseAwait="checkArchive"
              :buttonText="$t('checkArchive')"
              :disabled="!upcValue"
            ></LoaderButton>
          </v-flex>
        </v-layout>
        <v-layout pl-3 pr-5 justify-start align-baseline v-if="isInboundForecastDropshipping">
          <v-flex xs3>
            <label><strong>{{ $t('productDimensions') }}</strong></label>
          </v-flex>
          <v-flex xs3>
            <v-text-field
              hide-details
              class="required_field"
              v-model.number="productInEdit.weight"
              :rules="[
                fieldIsRequired('weight'),
                fieldIsOverZero('weight'),
              ]"
              :label="$t('weight')"
              :suffix="weightSuffix"
            >
            </v-text-field>
          </v-flex>
          <v-flex xs3>
            <v-text-field
              class="required_field"
              hide-details
              v-model.number="productInEdit.length"
              :label="$t('L')"
              :suffix="sizeSuffix"
              :rules="[fieldIsRequired('length'), fieldIsOverZero('length')]"
            ></v-text-field>
          </v-flex>
          <v-flex xs3>
            <v-text-field
              class="required_field"
              hide-details
              :label="$t('W')"
              :suffix="sizeSuffix"
              v-model.number="productInEdit.width"
              :rules="[fieldIsRequired('width'), fieldIsOverZero('width')]"
            ></v-text-field>
          </v-flex>
          <v-flex xs3>
            <v-text-field
              class="required_field"
              hide-details
              :suffix="sizeSuffix"
              :label="$t('H')"
              v-model.number="productInEdit.height"
              :rules="[fieldIsRequired('height'), fieldIsOverZero('height')]"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <ShippingRangeEditor
          v-if="isInboundForecastDropshipping"
          :value="shippingRanges"
          :materialFees="[]"
          :readonly="true"
          :unit="product.unit || tenantUnit"
        />
        <v-layout pl-3 pr-5 justify-start v-if="isInboundForecastDropshipping && !!product.warehouseLastUpdateTime && +product.warehouseLastUpdateTime > +product.lastModifiedTime">
          <v-flex ml-1>{{ $t('warehouseVerifiedDimensions') }} <v-icon color="success">check</v-icon></v-flex>
        </v-layout>
        <v-layout>
          <v-flex class="ml-3 confirm_upc">
            <v-checkbox 
              v-if="upcWarning" 
              :label="upcWarning" 
              v-model="isConfirmed" 
              color="blue" 
              class="productEditCheckBox" 
              name="confirmUpc" 
              :rules="[v => v || $t('confirmUpc')]"></v-checkbox>
          </v-flex>
        </v-layout>
        <v-layout id="product_note">
          <v-flex class="mx-3">
            <v-textarea
              :label="$t('note')"
              outline
              v-model="productInEdit.note"
              class="thinBox"></v-textarea>
          </v-flex>
        </v-layout>
      </v-container>
    </template>
  </FormSubmitPopup>
</template>

<script>
import FormSubmitPopup from './FormSubmitPopup'
import BarcodeFinder from '../utils/barcode'
import { buildShippingRanges, checkRules, cloneDeep, formatUpcString } from '@/utils/tools'
import LoaderButton from './LoaderButton'
import { measurementUnits, weightUnits } from '@/utils/const'
import ShippingRangeEditor from './ShippingRangeEditor'
import measurementMixin from '../mixins/measurement.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'productEdit',
  components: {
    LoaderButton,
    FormSubmitPopup,
    ShippingRangeEditor
  },
  mixins: [checkRules, measurementMixin],
  data () {
    return {
      rules: {
        upcValid: (value) => {
          return !value || !!value.match(/^\{.+\}$/g) || BarcodeFinder.isUpcValid(value) || this.$t('invalidUpc')
        },
        upcDup: (value) => {
          if (this.isLinkMode && this.isLinkExistProduct) return true
          let upcToCheck = formatUpcString(value)
          if (upcToCheck) {
            const isProductExist = this.products.some(product => {
              return product.id !== this.product.id && (product.upc || '') === (upcToCheck || '')
            })
            const isCombinedExist = this.combinedUpcSet.has(upcToCheck)
            return (isProductExist || isCombinedExist) ? this.$t('upcAlreadyExists') : true
          }
          return true
        }
      },
      isConfirmed: false,
      displayUPC: '',
      conditions: [
        { text: this.$t('new'), value: 'new' }, 
        { text: this.$t('used'), value: 'used' }, 
        { text: this.$t('refurbished'), value: 'refurbished' }
      ],
      isLinkExistProduct: false,
      productInEdit: {},
      productsWithoutUPC: [],
      isSaving: false,
      skuSet: {},
      checkSkuDup: (v) => {
        const dups = v.filter ? v.filter(item => this.skuSet.has(item) || this.combinedSkuSet.has(item)) : []
        return dups.length === 0 || this.$t('skuExists', { dups })
      },
      measurementUnits: measurementUnits,
      shippingRanges: []
    }
  },
  watch: {
    isLinkExistProduct (val) {
      this.$refs.UPC.validate()
      this.setProductInEdit()
    },
    products (value) {
      !this.isSaving && (this.productsWithoutUPC = this.products.filter(product => !(product.upc && product.upc !== '')))
    }
  },
  created () {
    this.skuSet = cloneDeep(this.$store.getters.skuSet)
    if (this.product.sku) {
      this.product.sku.forEach(item => {
        this.skuSet.delete(item)
      })
    }
    this.shippingRanges = buildShippingRanges(this.product, this.tenantUnit, this.buildMeasurement)
  },
  mounted () {
    this.isLinkExistProduct = this.isLinkMode
    this.setProductInEdit()
    this.productsWithoutUPC = this.products.filter(product => product.upc === this.product.upc || !(product.upc && product.upc !== ''))
    if (this.isInboundForecastDropshipping) {
      this.isConfirmed = true
      if (!this.productInEdit.unit) {
        this.productInEdit.unit = this.tenantUnit
      }
    }
  },
  computed: {
    ...mapGetters(['combinedUpcs']),
    combinedUpcSet () {
      return new Set(this.combinedUpcs
        .map(item => item.upc))
    },
    combinedSkuSet () {
      return new Set(this.combinedUpcs.reduce((acc, product) => {
        if (product.sku) {
          return [...acc, ...product.sku]
        }
        return acc
      }, []))
    },
    tenantUnit () {
      const isMeasurementMetric = this.$store.getters.isMeasurementMetric
      return isMeasurementMetric ? 'metric' : 'imperial'
    },
    unit () {
      return this.tenantUnit
    },
    isInboundForecastDropshipping () {
      const {featureInboundForecastDropshipping = []} = this.$store.getters.tenant || {}
      return !!featureInboundForecastDropshipping.length
    },
    upcValue () {
      return formatUpcString(this.displayUPC)
    },
    upcSet () {
      return new Set(this.products.map(item => item.upc))
    },
    isUPCChanged () {
      return this.upcValue !== this.productInEdit.upc
    },
    upcWarning () {
      if (this.isUPCChanged) {
        if (!this.displayUPC) {
          return this.$t('productEditConfirmWarning')
        } else {
          return this.$t('productEditConfirmWarningDisplayUpc')
        }
      } else {
        return ''
      }
    },
    products () {
      return this.$store.getters.products
    },
    sizeSuffix () {
      return measurementUnits[this.productInEdit.unit] || 'INCH'
    },
    weightSuffix () {
      return weightUnits[this.productInEdit.unit] || 'LB'
    }
  },
  methods: {
    formatUpcString,
    saveProduct () {
      this.productInEdit.upc = this.upcValue
      if (!this.productInEdit.upc) {
        this.productInEdit.upc = this.productInEdit.sku[0] || ''
      }
      if (this.productInEdit.approvedSkus) {
        // check whether approved sku being deleted
        const skuSet = new Set(this.productInEdit.sku || [])
        if (this.productInEdit.approvedSkus.some(item => !skuSet.has(item.split('_')[1]))) {
          throw Error('Approved sku can not be deleted.')
        }
      }

      this.productInEdit.sku.forEach(item => {
        if (this.skuSet.has(item)) throw Error(`Sku: ${item} has been assgined to another product.`)
      })

      let {isUpcChangeable, ...rest} = this.productInEdit
      this.isSaving = true
      return this.actionFunc(rest, true)
        .catch(error => {
          this.isSaving = false
          throw error
        })
    },
    async checkArchive (item) {
      const doc = await this.$store.dispatch('checkArchive', this.upcValue)
      if (doc) {
        if (confirm(this.$t('productEditCheckArchive'))) {
          if (this.products.length >= this.maxProductQty) {
            return alert(this.$t('productEditMaxProduct', { maxProductQty: this.maxProductQty }))
          }
          if (this.upcValue !== '' && this.upcSet.has(this.upcValue)) return alert(this.$t('productEditCantRestore'))
          await this.$store.dispatch('restoreProduct', {_key: doc.id, ...doc.data()})
          alert(this.$t('productEditRestoreProduct', { id: doc.id }))
        }
      }
    },
    selectExistProduct (product) {
      this.productInEdit = {...product, upc: this.productInEdit.upc}
    },
    setProductInEdit () {
      this.productInEdit = {...this.product}
      this.displayUPC = !this.productInEdit.upc || BarcodeFinder.isUpcValid(this.productInEdit.upc) ? this.productInEdit.upc : `{${this.productInEdit.upc}}`
      this.isConfirmed = false
      if (!this.productInEdit.condition) this.productInEdit.condition = this.conditions[0].value
      if (!this.productInEdit.price) this.productInEdit.price = 0
      if (this.productInEdit.isUpcChangeable === undefined) this.productInEdit.isUpcChangeable = true
      if (!this.productInEdit.sku) this.productInEdit.sku = []
    },
    customFilter (item, queryText, itemText) {
      const textOne = item.name ? item.name.toLowerCase() : ''
      const textTwo = item.id ? item.id.toLowerCase() : ''
      const searchText = queryText.toLowerCase()

      return textOne.indexOf(searchText) > -1 ||
        textTwo.indexOf(searchText) > -1
    }
  },
  props: {
    value: Boolean,
    title: String,
    editMode: {
      type: Boolean,
      default: false
    },
    actionFunc: Function,
    product: {
      type: Object,
      default: function () {
        return {upc: '', condition: this.conditions[0].value, isUpcChangeable: true}
      }
    },
    upcReadOnly: Boolean,
    isLinkMode: Boolean
  }
}
</script>

<style>
.x-small.btn  {
  height: 25px;
  width: 25px;
  margin: 5px 4px;
}

.thinBox .input-group__input {
  border: 1px solid rgba(0,0,0,.54) !important;
}

.productEditCheckBox label {
  color: #1976d2 !important;
}

.confirm_upc .input-group.input-group--selection-controls label {
  max-width: 95%;
}

#product_note {
  position: relative;
  top: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/ProductEdit.vue